// src/features/variant_workflow/clinical_observation/ReportPanelModal.tsx
import { Alert, Button, Divider, Input, Transfer } from 'antd';
import type { TransferItem } from 'antd/es/transfer';
import { Formik } from 'formik';
import React from 'react';
import { useSelector } from 'react-redux';

import FullPageLoader from '../../../foundation/components/full_page_loader/FullPageLoader';
import Modal from '../../../foundation/components/modal/Modal';
import { useVariantAccess } from '../hooks/useVariantAccess';
import { selectVariantLock } from '../redux/selectors';
import { VariantSample } from '../redux/types';
import { useReportPanelModalLogic } from './hooks';

type SampleModalType = {
  closeModal: () => void;
  sample?: VariantSample;
};

// Prepare data for Transfer
interface PanelTransferItem extends TransferItem {
  key: string;
  title: string;
}

// Form values type for Formik
interface ReportPanelFormValues {
  selected_panel_keys: string[];
}

// PanelTransferSelector subcomponent for clarity
const PanelTransferSelector = ({
  dataSource,
  selectedKeys,
  setSelectedKeys,
}: {
  dataSource: PanelTransferItem[];
  selectedKeys: string[];
  setSelectedKeys: (keys: string[]) => void;
}) => (
  <div className="panel-transfer">
    <div className="panel-transfer__labels">
      <div className="panel-transfer__label panel-transfer__label--left">
        Panels available
      </div>
      <div className="panel-transfer__spacer" />
      <div className="panel-transfer__label panel-transfer__label--right">
        Panels selected
      </div>
    </div>
    <Transfer
      dataSource={dataSource}
      targetKeys={selectedKeys}
      onChange={(nextTargetKeys) => setSelectedKeys(nextTargetKeys as string[])}
      render={(item) => item.title}
      showSearch
      locale={{
        searchPlaceholder: 'Enter search term to filter',
        itemUnit: 'panel',
        itemsUnit: 'panels',
      }}
      filterOption={(inputValue, item) =>
        (item.title || '').toLowerCase().includes(inputValue.toLowerCase())
      }
      style={{ width: '100%' }}
      listStyle={{ flex: 1, height: 300 }}
    />
  </div>
);

const ReportPanelModal = ({ closeModal, sample }: SampleModalType) => {
  const { isReadOnly } = useVariantAccess();
  const lock = useSelector(selectVariantLock);

  // All logic extracted to custom hook for separation of concerns
  const {
    isLoading,
    transferDataSource,
    modalOpts,
    initialValues,
    getIsSaveDisabled,
    handleFormSubmit,
  } = useReportPanelModalLogic({ closeModal, sample });

  return (
    <Modal {...modalOpts} className="h-proband-modal">
      {isLoading && <FullPageLoader />}

      {isReadOnly && lock && (
        <Alert
          message="Read-Only Mode"
          description={`This variant is currently locked by ${lock.locked_by} and cannot be edited.`}
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Formik
        initialValues={initialValues as ReportPanelFormValues}
        onSubmit={handleFormSubmit}
        enableReinitialize
        validateOnChange
        validateOnBlur
        validateOnMount
      >
        {({ values, handleSubmit, isValid, dirty, setFieldValue }) => {
          const isSaveDisabled = getIsSaveDisabled(isValid, dirty);

          return (
            <form onSubmit={handleSubmit}>
              <div className="c-form-field">
                <div className="c-form-field__label">Sample ID:</div>
                <div className="c-form-field__wrapper">
                  <Input
                    name="sample_external_id"
                    type="text"
                    value={sample?.sample_external_id}
                    disabled
                  />
                </div>
              </div>

              {/* Dual listbox for panels */}
              <div className="c-form-field">
                <div className="c-form-field__label">Report panel:</div>
                <div className="c-form-field__wrapper">
                  <PanelTransferSelector
                    dataSource={transferDataSource}
                    selectedKeys={values.selected_panel_keys}
                    setSelectedKeys={(keys) =>
                      setFieldValue('selected_panel_keys', keys)
                    }
                  />
                </div>
              </div>

              <Divider />

              <div className="c-form__btn-wrapper h-proband-modal__form-btn-wrapper">
                <Button
                  type="primary"
                  htmlType="submit"
                  className="h-proband-modal__form-submit-btn"
                  disabled={isSaveDisabled}
                >
                  Save changes
                </Button>
              </div>
            </form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default ReportPanelModal;
